import { FEATURES } from '../config/features';

/** The site's page identifiers. Lives here (not in Navigation) so the header
 *  and footer share one source and can't drift out of sync. */
export type MarketingPage =
  | 'home'
  | 'products'
  | 'recipes'
  | 'nutrition'
  | 'story'
  | 'team'
  | 'contact'
  | 'club';

export interface NavItem {
  page: MarketingPage;
  /** Real URL path. Was a `#hash` until 2026-08-30 — Google discards
   *  fragments, so the whole site was one indexable address. */
  path: string;
  label: string;
  /** Page <title> suffix and meta description, per route. */
  title: string;
  description: string;
  /** Kept out of the index for now: the recipes aren't ready to be found
   *  (Jarvis, 2026-08-30). Crawling is still allowed so the noindex is
   *  actually seen; the page is simply absent from sitemap.xml. */
  noindex?: boolean;
}

const ALL_NAV_ITEMS: NavItem[] = [
  {
    page: 'home',
    path: '/',
    label: 'Home',
    title: 'TrueNooch™ — an ingredient you cook with',
    description:
      'TrueNooch™ is an ingredient you cook with — a plant-based whole food of eight real ingredients. A Round you slice, cubes, and a flour you bake with. Made in Denver, Colorado.',
  },
  {
    page: 'club',
    path: '/club',
    label: 'Order Direct',
    title: 'Order TrueNooch™ direct',
    description:
      'Order TrueNooch™ direct from the kitchen in Denver. Join the club for first dibs on each small batch.',
  },
  {
    page: 'products',
    path: '/products',
    label: 'Products',
    title: 'TrueNooch™ — the Round, the cubes, and the flour',
    description:
      'The forms TrueNooch™ comes in: a firm sliceable Round wrapped in paper, cubes that travel, and TrueNooch™ Flour — the same eight ingredients, for the things you already bake. Made in Denver, Colorado.',
  },
  {
    page: 'recipes',
    path: '/recipes',
    label: 'Recipes',
    title: 'Cooking at home with TrueNooch™',
    description:
      'Ways to cook with TrueNooch™ — cold on a sandwich, browned in a pan, or worked into dough.',
    noindex: true,
  },
  {
    page: 'nutrition',
    path: '/nutrition',
    label: 'Nutrition',
    title: 'Nutrition — macro-balanced, and sliceable',
    description:
      'What is in TrueNooch™ and how its macros stack up: a short list of real ingredients cooked into one whole food, balanced across protein, carbs and fat.',
  },
  {
    page: 'story',
    path: '/story',
    label: 'Story',
    title: 'The TrueNooch™ story',
    description:
      'How TrueNooch™ got here — from a yoga resort kitchen to a home kitchen in Denver. Eating it, sharing it, sharing more of it.',
  },
  {
    page: 'team',
    path: '/team',
    label: 'Team',
    title: 'The team behind TrueNooch™',
    description:
      'A small team in Colorado building TrueNooch™ from first principles.',
  },
  {
    page: 'contact',
    path: '/contact',
    label: 'Contact',
    title: 'Contact TrueNooch™',
    description: 'Get in touch with TrueNooch™ — Denver, Colorado.',
  },
];

// Feature-gated visible items — consumed by BOTH the header nav and the footer nav.
export const NAV_ITEMS: NavItem[] = ALL_NAV_ITEMS.filter((item) => {
  if (item.page === 'club') return FEATURES.club;
  return true;
});

/** Every route, feature gating aside — the router and the meta lookup need
 *  the full set, not just what the nav chooses to show. */
export const ALL_PAGES: NavItem[] = ALL_NAV_ITEMS;

/** Pages that belong in sitemap.xml. Kept here so the sitemap and the
 *  noindex flag can't drift apart. */
export const INDEXABLE_PAGES: NavItem[] = ALL_NAV_ITEMS.filter((p) => !p.noindex);

/** Old `#hash` URLs, still live in anything already shared. Mapped so we can
 *  redirect rather than drop people on the home page. */
export const LEGACY_HASH_TO_PATH: Record<string, string> = {
  '#home': '/',
  '#site': '/',
  '#club': '/club',
  '#products': '/products',
  '#recipes': '/recipes',
  '#nutrition': '/nutrition',
  '#story': '/story',
  '#team': '/team',
  '#contact': '/contact',
};
